import React, { useEffect, useMemo, useState } from 'react';
import './AdminInventoryManagement.css';

const PAGE_SIZE = 20;

const AdminInventoryTable = ({ refreshTick = 0 }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [category, setCategory] = useState('');
  const [allVendors, setAllVendors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editOnHand, setEditOnHand] = useState('');
  const [editReserved, setEditReserved] = useState('');
  const [showHistoryFor, setShowHistoryFor] = useState(null);
  const [movements, setMovements] = useState([]);
  const [moveTotal, setMoveTotal] = useState(0);
  const [movePage, setMovePage] = useState(1);
  const MOVE_PAGE_SIZE = 20;

  const adminKey = useMemo(() => window.localStorage.getItem('ADMIN_API_KEY') || window.localStorage.getItem('adminApiKey') || window.localStorage.getItem('token'), []);

  const fetchVendors = async () => {
    try {
      const response = await fetch('/api/admin/vendors?limit=1000', {
        headers: { 'x-admin-key': adminKey }
      });
      if (response.ok) {
        const data = await response.json();
        setAllVendors(data.items || []);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchRows = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
        ...(search && { search }),
        // backend expects product status (active/inactive)
        ...(status && { status }),
        ...(category && { category }),
        ...(vendorId && { vendor_id: vendorId })
      });
      // Use admin products endpoint which includes vendor details and inventory joins
      const res = await fetch(`/api/admin/products?${params.toString()}`, {
        headers: { 'x-admin-key': adminKey }
      });
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
            ? data.items
            : Array.isArray(data?.products)
              ? data.products
              : Array.isArray(data?.[0])
                ? data[0]
                : [];
        setRows(list);
        const computedTotal = typeof data?.total === 'number' ? data.total : list.length;
        setTotal(computedTotal);
      } else {
        // Fallback to general inventory endpoint (vendor-scoped); this will likely require vendorId
        const res2 = await fetch(`/api/inventory?${params.toString()}`, {
          headers: { Authorization: `Bearer ${adminKey}` }
        });
        const data2 = res2.ok ? await res2.json() : [];
        const list2 = Array.isArray(data2) ? data2 : Array.isArray(data2?.items) ? data2.items : Array.isArray(data2?.[0]) ? data2[0] : [];
        setRows(list2);
        setTotal(list2.length);
      }
    } catch (e) {
      console.error('Failed to load inventory list', e);
      setRows([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, [page, status, category, vendorId, refreshTick]);

  useEffect(() => {
    fetchVendors();
  }, []);

  const startEdit = (row) => {
    setEditingId(row.product_id || row.id);
    setEditOnHand(String(row.stock_on_hand ?? row.stockOnHand ?? 0));
    setEditReserved(String(row.stock_reserved ?? row.stockReserved ?? 0));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditOnHand('');
    setEditReserved('');
  };

  const saveStock = async (row) => {
    try {
      const productId = row.product_id || row.id;
      const vendor = row.vendor_id || vendorId;
      if (!vendor) {
        alert('Please specify vendor to update stock.');
        return;
      }
      // Update both on_hand and reserved via admin products endpoint (partial update)
      const bodyAdmin = { stock_on_hand: Number(editOnHand), stock_reserved: Number(editReserved) };
      const respAdmin = await fetch(`/api/admin/products/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-admin-key': adminKey },
        body: JSON.stringify(bodyAdmin)
      });
      if (respAdmin.ok) {
        cancelEdit();
        fetchRows();
      } else {
        let errText = '';
        try { const err = await respAdmin.json(); errText = err?.error || err?.message || ''; } catch (_) {}
        alert(errText || 'Failed to update stock');
      }
    } catch (e) {
      console.error('saveStock error', e);
      alert('Failed to update stock');
    }
  };

  const openHistory = async (row) => {
    setShowHistoryFor(row);
    setMovePage(1);
    await fetchMovements(row, 1);
  };

  const fetchMovements = async (row, pageNum = movePage) => {
    try {
      const productId = row.product_id || row.id;
      const params = new URLSearchParams({ product_id: String(productId), page: String(pageNum), limit: String(MOVE_PAGE_SIZE) });
      const res = await fetch(`/api/admin/inventory/movements?${params.toString()}`, { headers: { 'x-admin-key': adminKey } });
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data?.movements) ? data.movements : Array.isArray(data) ? data : [];
        setMovements(list);
        setMoveTotal(typeof data?.total === 'number' ? data.total : list.length);
      } else {
        setMovements([]);
        setMoveTotal(0);
      }
    } catch (_) {
      setMovements([]);
      setMoveTotal(0);
    }
  };

  const exportMovements = async (row) => {
    try {
      const productId = row.product_id || row.id;
      const url = `/api/admin/inventory/movements/export?product_id=${productId}`;
      const res = await fetch(url, { headers: { 'x-admin-key': adminKey } });
      if (!res.ok) return;
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `stock_movements_${productId}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (_) {}
  };

  const toggleStatus = async (row, nextStatus) => {
    try {
      const productId = row.product_id || row.id;
      if (String(nextStatus).toLowerCase() === 'inactive') {
        const ok = window.confirm('Deactivate this product? It will be hidden from the vendor catalog.');
        if (!ok) return;
      }
      const resp = await fetch(`/api/admin/products/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-admin-key': adminKey },
        body: JSON.stringify({ status: String(nextStatus).toLowerCase() })
      });
      if (resp.ok) {
        fetchRows();
      } else {
        const err = await resp.json();
        alert(err.error || 'Failed to update status');
      }
    } catch (e) {
      console.error('toggleStatus error', e);
      alert('Failed to update status');
    }
  };

  const deleteProduct = async (row) => {
    if (!confirm('Delete this product permanently? This cannot be undone.')) return;
    try {
      const productId = row.product_id || row.id;
      const resp = await fetch(`/api/admin/products/${productId}`, {
        method: 'DELETE',
        headers: { 'x-admin-key': adminKey }
      });
      if (resp.ok) {
        fetchRows();
      } else {
        const err = await resp.json();
        alert(err.error || 'Failed to delete product');
      }
    } catch (e) {
      console.error('deleteProduct error', e);
      alert('Failed to delete product');
    }
  };

  const onSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchRows();
  };

  return (
    <div className="admin-inventory-table">
      <form className="filters-section" onSubmit={onSearch}>
        <div className="filter-group">
          <input
            className="form-input"
            placeholder="Search name or SKU"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All Product Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="discontinued">Discontinued</option>
          </select>
        </div>
        <div className="filter-group">
          <input
            className="form-input"
            placeholder="Category (optional)"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select 
            className="form-select" 
            value={vendorId} 
            onChange={(e) => setVendorId(e.target.value)}
          >
            <option value="">All Vendors ({allVendors.length})</option>
            {allVendors.map(vendor => (
              <option key={vendor.id} value={vendor.id}>
                {vendor.shop_name || vendor.business_name} (ID: {vendor.id})
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <button className="btn btn-primary" type="submit">Search</button>
        </div>
      </form>

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Product Name</th>
              <th>Vendor</th>
              <th>Stock On Hand</th>
              <th>Reserved</th>
              <th>Available</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan="7" className="loading-cell">Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="7" className="empty-cell">No products found</td></tr>
            ) : (
              rows.map((row) => {
                const id = row.product_id || row.id;
                const isEditing = editingId === id;
                const normalizedStatus = String(row.status || '').toLowerCase();
                const nextStatus = (normalizedStatus === 'active') ? 'inactive' : 'active';
                const stockOnHandVal = Number(row.stock_on_hand ?? row.stockOnHand ?? 0);
                const stockReservedVal = Number(row.stock_reserved ?? row.stockReserved ?? 0);
                const stockAvailableVal = Math.max(0, stockOnHandVal - stockReservedVal);
                return (
                  <tr key={id}>
                    <td>{row.name}</td>
                    <td>{(row.vendor_name ? `${row.vendor_name}` : '-')}{row.vendor_id ? ` (${row.vendor_id})` : (vendorId ? ` (${vendorId})` : '')}</td>
                    <td>
                      {isEditing ? (
                        <input className="form-input" style={{ width: 90 }} value={editOnHand} onChange={(e) => setEditOnHand(e.target.value)} />
                      ) : (
                        <span>{stockOnHandVal}</span>
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <input className="form-input" style={{ width: 90 }} value={editReserved} onChange={(e) => setEditReserved(e.target.value)} />
                      ) : (
                        <span>{stockReservedVal}</span>
                      )}
                    </td>
                    <td><span>{stockAvailableVal}</span></td>
                    <td>{normalizedStatus}</td>
                    <td>
                      {!isEditing && (
                        <>
                          <button className="btn btn-secondary btn-sm" onClick={() => startEdit(row)}>Edit</button>
                          <button className="btn btn-secondary btn-sm" onClick={() => toggleStatus(row, nextStatus)}>
                            {nextStatus === 'active' ? 'Activate' : 'Deactivate'}
                          </button>
                          <button className="btn btn-danger btn-sm" onClick={() => deleteProduct(row)}>Delete</button>
                          <button className="btn btn-secondary btn-sm" onClick={() => openHistory(row)}>History</button>
                        </>
                      )}
                      {isEditing && (
                        <div className="inline-edit">
                          <button className="btn btn-primary btn-sm" onClick={() => saveStock(row)}>Save</button>
                          <button className="btn btn-secondary btn-sm" onClick={cancelEdit}>Cancel</button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <button className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
        <span className="page-info">Page {page}</span>
        <button className="btn btn-secondary" onClick={() => setPage((p) => p + 1)}>Next</button>
      </div>

      {showHistoryFor && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 900 }}>
            <div className="modal-header">
              <h2>Stock Movements — {showHistoryFor.name} (ID {showHistoryFor.id || showHistoryFor.product_id})</h2>
              <button className="close-btn" onClick={() => setShowHistoryFor(null)}>✕</button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <div>
                <button className="btn btn-secondary" onClick={() => exportMovements(showHistoryFor)}>Export CSV</button>
              </div>
              <div className="pagination">
                <button className="btn btn-secondary" disabled={movePage <= 1} onClick={async () => { const next = Math.max(1, movePage - 1); setMovePage(next); await fetchMovements(showHistoryFor, next); }}>Prev</button>
                <span className="page-info">Page {movePage} of {Math.max(1, Math.ceil(moveTotal / MOVE_PAGE_SIZE))}</span>
                <button className="btn btn-secondary" disabled={movePage >= Math.ceil(moveTotal / MOVE_PAGE_SIZE)} onClick={async () => { const next = movePage + 1; setMovePage(next); await fetchMovements(showHistoryFor, next); }}>Next</button>
              </div>
            </div>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Quantity</th>
                    <th>Reference</th>
                    <th>Notes</th>
                    <th>By</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.length === 0 ? (
                    <tr><td colSpan="6" className="empty-cell">No movements</td></tr>
                  ) : movements.map(m => (
                    <tr key={m.id}>
                      <td>{m.created_at ? new Date(m.created_at).toLocaleString() : ''}</td>
                      <td>{m.movement_type}</td>
                      <td>{m.quantity}</td>
                      <td>{m.reference_type}{m.reference_id ? ` #${m.reference_id}` : ''}</td>
                      <td>{m.notes || ''}</td>
                      <td>{m.created_by || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInventoryTable;


